'use client';

import { useInfoTopBannerQuery } from '@/features/info-top-banner/api/info-top-banner-settings';
import { CloseBanner } from '@/features/info-top-banner/components/close-banner';
import { usePathname } from '@/navigation';
import { useResizeObserver } from '@react-hookz/web';
import { clsx } from 'clsx';
import { useEffect, useRef, useState } from 'react';

import { TextContent } from '@/features/info-top-banner/components/text-content';
import { useCheckBannerVisibility } from '@/features/info-top-banner/hooks/use-check-banner-visibility';

const variants: Record<'black' | 'red', string> = {
  black: 'bg-contrast  py-xxs',
  red: 'bg-brand pt-xxs',
};

interface Props {
  isClosed: boolean;
}

export const InfoTopBannerClient = ({ isClosed }: Props) => {
  const { data } = useInfoTopBannerQuery(!isClosed);
  const ref = useRef<HTMLDivElement | null>(null);
  const [wrapperHeight, setWrapperHeight] = useState(0);
  const [closed, setClosed] = useState(false);

  const pathname = usePathname();

  const isBannerActive = useCheckBannerVisibility({
    active: data?.active ?? false,
    visibleFrom: data?.visibleFrom?.date,
    visibleTo: data?.visibleTo?.date,
  });

  const hidden = isClosed || !isBannerActive || pathname.includes('basket');

  const resizeCallback = () => {
    if (!ref.current) return;
    setWrapperHeight(ref.current.getBoundingClientRect().height);
  };

  useEffect(() => {
    resizeCallback();
  }, []);

  useResizeObserver(ref?.current ?? null, resizeCallback, !hidden && !closed);

  const handleCloseBanner = () => {
    setClosed(true);
  };

  if (hidden || !data) return null;

  return (
    <div
      ref={ref}
      className={clsx(
        'anim-pop-out grid w-full items-center font-normal text-inverted',
        variants[data.color ?? 'black'],
        'invisible',
      )}
      style={{
        marginTop: closed ? `-${wrapperHeight}px` : 0,
        visibility: closed ? 'hidden' : 'visible',
        transition: closed
          ? 'margin-top 250ms ease-in, visibility 0ms linear 250ms'
          : 'margin-top 0ms ease-in',
      }}
    >
      <div className="container flex items-center m:justify-center">
        <TextContent
          content={data.content}
          url={data.url}
          promoCode={data.promoCode}
        />
        {data.closeable && (
          <CloseBanner handleClose={handleCloseBanner} variant={data.color} />
        )}
      </div>
    </div>
  );
};
